/**
 * Convert CrawledStaffMember → ProviderInfo so the unified card can render
 * crawl results without a separate component.
 */
import type { CrawledStaffMember, ProviderInfo } from '../types';

/** Split a crawled "Firstname Lastname, MA, LMFT" into first/last. */
function splitCrawlName(name: string): { first: string; last: string } {
  const cleaned = name.replace(/,.*$/, '').trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

export function crawlStaffToProviderInfo(
  member: CrawledStaffMember,
): ProviderInfo {
  const { first, last } = splitCrawlName(member.name);
  return {
    first_name: first,
    last_name: last,
    email: '',
    role: member.role ?? '',
    roles: member.roles?.length ? member.roles : member.role ? [member.role] : [],
    license_type: Array.isArray(member.license_type) ? member.license_type : member.license_type ? [member.license_type] : [],
    degrees: Array.isArray(member.degrees) ? member.degrees : member.degrees ? [member.degrees] : [],
    npi_number: '',
    pay_type: '',
    timesheet_mode: 'full_time',
    specialties: member.specialties ?? [],
    bio: member.bio ?? '',
    photo_url: member.photo_url ?? '',
    photo_url_external: member.photo_url_external ?? null,
    website_url: member.website_url ?? '',
    title: member.title ?? '',
    age_range: member.age_range ?? undefined,
    supervisor_name: member.supervisor_name ?? '',
    assigned_provider_names: member.assigned_provider_names ?? [],
    from_crawl: true,
    _source: 'crawl' as const,
  };
}

/** Convert an array of crawled staff to ProviderInfo[]. */
export function crawlStaffArrayToProviders(
  staff: CrawledStaffMember[],
): ProviderInfo[] {
  return staff.map(crawlStaffToProviderInfo);
}

/** Convert a ProviderInfo back to CrawledStaffMember for backend payload. */
export function providerInfoToCrawlStaff(
  provider: ProviderInfo,
): CrawledStaffMember {
  const name = [provider.first_name, provider.last_name].filter(Boolean).join(' ');
  return {
    name,
    title: provider.title ?? null,
    role: provider.roles?.[0] ?? provider.role ?? null,
    roles: provider.roles ?? [],
    license_type: provider.license_type ?? [],
    degrees: provider.degrees ?? [],
    specialties: provider.specialties ?? [],
    age_range: provider.age_range ?? null,
    bio: provider.bio ?? null,
    photo_url: provider.photo_url ?? null,
    photo_url_external: provider.photo_url_external ?? null,
    website_url: provider.website_url ?? null,
    supervisor_name: provider.supervisor_name,
    assigned_provider_names: provider.assigned_provider_names,
  };
}
